import React, { createContext, useContext, useReducer, useEffect } from 'react';

interface Artwork {
    id: string;
    title: string;
    imageUrl: string;
    type?: string;
}

interface InquiryState {
    items: Artwork[];
}

const InquiryContext = createContext<{
    state: InquiryState;
    addItem: (item: Artwork) => void;
    removeItem: (id: string) => void;
    clearInquiry: () => void;
} | undefined>(undefined);

const initialState: InquiryState = {
    items: JSON.parse(localStorage.getItem('kunstlounge_inquiry') || '[]'),
};

const inquiryReducer = (state: InquiryState, action: any) => {
    switch (action.type) {
        case 'ADD_ITEM':
            if (state.items.find(item => item.id === action.payload.id)) return state;
            const updatedItems = [...state.items, action.payload];
            localStorage.setItem('kunstlounge_inquiry', JSON.stringify(updatedItems));
            return { items: updatedItems };
        case 'REMOVE_ITEM':
            const filteredItems = state.items.filter(item => item.id !== action.payload.id);
            localStorage.setItem('kunstlounge_inquiry', JSON.stringify(filteredItems));
            return { items: filteredItems };
        case 'CLEAR':
            localStorage.removeItem('kunstlounge_inquiry');
            return { items: [] };
        default:
            return state;
    }
};

export const InquiryProvider: React.FC = ({ children }) => {
    const [state, dispatch] = useReducer(inquiryReducer, initialState);

    const addItem = (item: Artwork) => {
        dispatch({ type: 'ADD_ITEM', payload: item });
    };

    const removeItem = (id: string) => {
        dispatch({ type: 'REMOVE_ITEM', payload: { id } });
    };

    const clearInquiry = () => {
        dispatch({ type: 'CLEAR' });
    };

    return (
        <InquiryContext.Provider value={{ state, addItem, removeItem, clearInquiry }}>
            {children}
        </InquiryContext.Provider>
    );
};

export const useInquiry = () => {
    const context = useContext(InquiryContext);
    if (context === undefined) {
        throw new Error('useInquiry must be used within an InquiryProvider');
    }
    return context;
};
